import {
  buildSearchSemanticQuery,
  hasTokenBoundaryPhraseMatch,
  normalizeBlogSearchText,
} from "@/lib/blog/search-lexical";
import {
  buildSearchQueryEntityProfile,
  evaluateSearchSemanticText,
  inferSearchIntent,
  type SearchSemanticText,
} from "@/lib/blog/search-semantics";

export type SearchCandidate<TMeta> = {
  id: string;
  index: number;
  label: string;
  meta: TMeta;
  scoreBoost?: number;
  searchText?: string;
  semanticText?: Partial<SearchSemanticText>;
};

export type RankedSearchCandidate<TMeta> = {
  candidate: SearchCandidate<TMeta>;
  normalizedSearchText: string;
  score: number;
};

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function hasTermBoundaryMatch(text: string, term: string): boolean {
  if (!term) return false;
  return new RegExp(`(?:^|\\s)${escapeRegex(term)}(?:\\s|$)`).test(text);
}

function buildCandidateSemanticText<TMeta>(
  candidate: SearchCandidate<TMeta>
): SearchSemanticText {
  const provided = candidate.semanticText ?? {};
  const titleText = normalizeBlogSearchText(provided.titleText ?? candidate.label);
  return {
    filenameText: normalizeBlogSearchText(provided.filenameText ?? candidate.id),
    titleText,
    descriptionText: normalizeBlogSearchText(provided.descriptionText ?? ""),
    categoryText: normalizeBlogSearchText(provided.categoryText ?? ""),
    subjectText: normalizeBlogSearchText(provided.subjectText ?? ""),
    bodyText: normalizeBlogSearchText(
      provided.bodyText ?? candidate.searchText ?? candidate.label
    ),
  };
}

function scoreSearchCandidateText({
  explanationTerms,
  normalizedCandidate,
  normalizedQuery,
  semanticBoost,
}: {
  explanationTerms: readonly string[];
  normalizedCandidate: string;
  normalizedQuery: string;
  semanticBoost: number;
}): number {
  if (!normalizedCandidate) return 0;
  if (!normalizedQuery) return 1 + semanticBoost;

  let score = 0;
  const allowSubstringQuery = normalizedQuery.length > 2;

  if (normalizedCandidate === normalizedQuery) score += 220;
  if (normalizedCandidate.startsWith(normalizedQuery)) score += 160;
  if (hasTokenBoundaryPhraseMatch(normalizedCandidate, normalizedQuery)) {
    score += 100;
  } else if (allowSubstringQuery && normalizedCandidate.includes(normalizedQuery)) {
    score += 80;
  }

  const overlap = explanationTerms.reduce(
    (count, term) =>
      count + (hasTermBoundaryMatch(normalizedCandidate, term) ? 1 : 0),
    0
  );

  score += overlap * 18;
  return score + semanticBoost;
}

export function rankSearchCandidatesStatic<TMeta>({
  candidates,
  maxItems,
  query,
}: {
  candidates: readonly SearchCandidate<TMeta>[];
  maxItems: number;
  query: string;
}): RankedSearchCandidate<TMeta>[] {
  const semanticQuery = buildSearchSemanticQuery(query);
  const { explanationTerms, normalizedQuery, queryTerms } = semanticQuery;
  const intent = inferSearchIntent(queryTerms);
  const queryEntities = buildSearchQueryEntityProfile({
    normalizedQuery,
    queryTerms,
    intent,
  });

  if (!normalizedQuery) {
    return candidates.slice(0, maxItems).map((candidate) => ({
      candidate,
      normalizedSearchText: normalizeBlogSearchText(
        candidate.searchText ?? candidate.label
      ),
      score: 1_000 + Math.min(Math.max(candidate.scoreBoost ?? 0, 0), 120),
    }));
  }

  return candidates
    .map((candidate) => {
      const normalizedSearchText = normalizeBlogSearchText(
        candidate.searchText ?? candidate.label
      );
      const semanticText = buildCandidateSemanticText(candidate);
      const semanticEvaluation = evaluateSearchSemanticText({
        intent,
        queryEntities,
        text: semanticText,
      });
      const score =
        scoreSearchCandidateText({
          explanationTerms,
          normalizedCandidate: normalizedSearchText,
          normalizedQuery,
          semanticBoost:
            semanticEvaluation.intentBoost + semanticEvaluation.entityBoost,
        }) + Math.min(Math.max(candidate.scoreBoost ?? 0, 0), 120);

      if (score <= 0 || !semanticEvaluation.passesIntentGate) return null;

      return {
        candidate,
        normalizedSearchText,
        score,
      };
    })
    .filter(
      (entry): entry is RankedSearchCandidate<TMeta> => entry !== null
    )
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.candidate.index - b.candidate.index ||
        a.candidate.label.localeCompare(b.candidate.label)
    )
    .slice(0, maxItems);
}
